import {
  CouponsEntrySection,
  HighTierCouponBanner as HighTierCouponBannerType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useHistory} from '@aboutyou/router';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {HeightTransition} from '@aboutyou/transitions';
import {CouponBanner as CouponBannerBase} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import {getIsExpiryDateLessThan} from '../../CouponCard/helpers/getIsExpiryDateLessThan';
import {
  UrgencyLabelLessThan12Hours,
  UrgencyLabelLessThan5Days,
} from '../CouponUrgencyLabel/CouponUrgencyLabel';
import {hasAppOnlyRedeemableDevices} from '../utils/hasAppOnlyRedeemableDevices';

const HeightTransitionWrapper = (
  props: React.PropsWithChildren<{
    className?: string;
    disableAnimation?: boolean;
  }>,
) => {
  if (props.disableAnimation) {
    return <div className={props.className}>{props.children}</div>;
  }
  return (
    <HeightTransition duration={200} mounted={true} className={props.className}>
      {props.children}
    </HeightTransition>
  );
};

export const CouponBanner = (props: {
  banner:
    | HighTierCouponBannerType
    | (CouponsEntrySection & {
        couponExpiryDate?: HighTierCouponBannerType['couponExpiryDate'];
        redeemableDevices?: HighTierCouponBannerType['redeemableDevices'];
      });
  ['data-testid']?: string;
  className?: string;
  enableUrgencyLabel?: boolean;
  disableAnimation?: boolean;
}) => {
  const {banner, className, disableAnimation, enableUrgencyLabel} = props;
  const history = useHistory();

  const trackFeature = useTrackFeature();

  const isExpiryDateLessThan12Hours = getIsExpiryDateLessThan({
    expiryDate: banner.couponExpiryDate,
    unit: 'hours',
    threshold: 12,
  });

  const isExpiryDateLessThan5Days = getIsExpiryDateLessThan({
    expiryDate: banner.couponExpiryDate,
    unit: 'days',
    threshold: 5,
  });

  const featureLabel =
    isExpiryDateLessThan5Days || isExpiryDateLessThan12Hours
      ? `${banner.featureTracker.label}|urgent`
      : banner.featureTracker.label;

  const isAppOnlyCoupon = banner.redeemableDevices
    ? hasAppOnlyRedeemableDevices(banner.redeemableDevices)
    : false;

  const onClick = () => {
    trackFeature({
      label: featureLabel,
      name: banner.featureTracker.name,
      eventType: 'click',
    });
    history.push(banner.link.url || '');
  };

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: featureLabel,
    name: banner.featureTracker.name,
  });

  return (
    <div ref={ref}>
      <HeightTransitionWrapper
        disableAnimation={disableAnimation}
        className={className}
      >
        <CouponBannerBase
          data-testid={props['data-testid'] || 'CouponBanner'}
          barText={
            <CouponUrgencyLabel
              couponExpiryDate={banner.couponExpiryDate}
              enableUrgencyLabel={!isAppOnlyCoupon && enableUrgencyLabel}
              header={banner.header}
            />
          }
          headline={banner.headline}
          subline={banner.subline}
          conditionText={banner.info?.body}
          onClick={onClick}
          setGradientBackground={
            enableUrgencyLabel &&
            !isAppOnlyCoupon &&
            isExpiryDateLessThan12Hours
          }
        />
      </HeightTransitionWrapper>
    </div>
  );
};

const CouponUrgencyLabel: FC<{
  header: string;
  couponExpiryDate?: Date;
  enableUrgencyLabel?: boolean;
}> = ({couponExpiryDate, header, enableUrgencyLabel}) => {
  const isExpiryDateLessThan5Days = getIsExpiryDateLessThan({
    expiryDate: couponExpiryDate,
    unit: 'days',
    threshold: 5,
  });

  const isExpiryDateLessThan12Hours = getIsExpiryDateLessThan({
    expiryDate: couponExpiryDate,
    unit: 'hours',
    threshold: 12,
  });

  if (
    !enableUrgencyLabel ||
    !couponExpiryDate ||
    (!isExpiryDateLessThan5Days && !isExpiryDateLessThan12Hours)
  ) {
    return <>{header}</>;
  }

  if (isExpiryDateLessThan12Hours) {
    return <UrgencyLabelLessThan12Hours couponExpiryDate={couponExpiryDate} />;
  }

  return <UrgencyLabelLessThan5Days couponExpiryDate={couponExpiryDate} />;
};
